import type { Delivery, MediaView } from "./mediaTypes";
import { safeDeliveryUrl } from "./mediaPresets";
import { mediaForUrl } from "./mediaReferences";

const urlOf = (item: any): string =>
  typeof item === "string" ? item : String(item?.blobImage || item?.mediaURL || item?.mediaUrl || "");

/**
 * Every copy known for a photo the user opened full screen: the media-service view the API attached to the
 * row, or one registered for its link. Old S3 photos are looked up with `fetchLegacyCopies`.
 */
export function fullViewMedia(item: any): MediaView | undefined {
  if (!item) return undefined;
  const attached = typeof item === "object" ? item.mediaDelivery : undefined;
  if (attached?.derivatives?.length) return attached as MediaView;
  return mediaForUrl(urlOf(item));
}

/** A photo stored as a plain S3 link (or one of its copies). Proxied catalogue links and the CDN never match. */
export const isStoredS3Photo = (url: string): boolean =>
  /^https:\/\/[^/?#]+\.s3\.[a-z0-9-]+\.amazonaws\.com\//i.test(url);

const lookups = new Map<string, Promise<MediaView | undefined>>();

/** The copies of an old S3 photo, with real sizes, from the public lookup; once per link per page load. */
export function fetchLegacyCopies(url: string): Promise<MediaView | undefined> {
  const key = url.split("?")[0];
  let lookup = lookups.get(key);
  if (!lookup) {
    const base = String(process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");
    lookup = fetch(`${base}/public/media/copies?url=${encodeURIComponent(key)}`)
      .then((response) => response.json())
      .then((body) => {
        const derivatives: Delivery[] = (body?.data?.derivatives || []).filter((image: Delivery) => safeDeliveryUrl(image?.url));
        return derivatives.length
          ? { mediaId: "", operationalReady: true, archiveStatus: "", masterUrl: null, derivatives, aiUsage: "" }
          : undefined;
      })
      .catch(() => {
        lookups.delete(key);
        return undefined;
      });
    lookups.set(key, lookup);
  }
  return lookup;
}

/** The small copy shown at once while the sharp one loads; none when the smallest copy is not small. */
export function softPreviewUrl(media: MediaView | undefined): string | undefined {
  const smallest = (media?.derivatives || [])
    .filter((image) => safeDeliveryUrl(image.url))
    .sort((a, b) => a.width - b.width)[0];
  return smallest && smallest.width <= 480 ? smallest.url : undefined;
}

/**
 * The width the photo is drawn at, for `sizes`, so the browser fetches what the screen needs. Mirrors
 * `.hkp-media-zoom.is-portrait` / `.is-landscape` in producttheme.scss: a portrait photo is limited by
 * height and drawn far narrower than the screen. Unknown shape: 96vw.
 */
export function viewerSizes(media: MediaView | undefined, zoomed = false): string {
  const largest = (media?.derivatives || []).filter((image) => image.width > 0 && image.height > 0)
    .sort((a, b) => b.width - a.width)[0];
  if (!largest || typeof window === "undefined") return zoomed ? "192vw" : "96vw";
  const ratio = largest.width / largest.height;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const drawn = ratio < 1
    ? Math.min(Math.min(0.88 * vh, 920) * ratio, 0.96 * vw)
    : Math.min(0.96 * vw, 1280, 0.82 * vh * ratio);
  return Math.ceil(drawn * (zoomed ? 2 : 1)) + "px";
}

/** Data saver on, or a 2G/3G link (Chrome and Android report this; Safari does not, so it reads as fast). */
export function slowConnection(): boolean {
  const connection = typeof navigator === "undefined" ? undefined : (navigator as any).connection;
  return Boolean(connection?.saveData) || /(^|-)(2g|3g)$/.test(String(connection?.effectiveType || ""));
}
